import { useEffect, useState } from "react";
import { CheckCircle2, FileText, Trash2, RotateCcw, XCircle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { useConfirm } from "@/components/ui/Confirm";
import {
  listAnsweredGaps,
  reopenGap,
  type AnsweredGap,
  type ResolutionFilter,
} from "@/lib/api";
import { relative, absolute } from "@/lib/format";
import { cn } from "@/lib/cn";

interface AnsweredFeedProps {
  /** Sub-filter by status; "all" shows both answered and dismissed. */
  filter: ResolutionFilter;
  /** Bumped from outside to force a refetch (e.g. after a new resolution). */
  refreshKey: number;
  /** Notify parent so it can reload the open list when something is reopened. */
  onChanged?: () => void;
}

/**
 * Resolved feed. Shows BOTH answered clusters (with the resulting FAQ doc)
 * AND dismissed clusters (with no document). Owner can reopen either kind
 * — for dismissed, that just puts the cluster back in Open; for answered,
 * the underlying FAQ document is preserved (deleting embeddings would
 * silently break grounding).
 */
export function AnsweredFeed({ filter, refreshKey, onChanged }: AnsweredFeedProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems]   = useState<AnsweredGap[] | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [reopening, setReopening] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    listAnsweredGaps(filter)
      .then((r) => { if (!cancelled) setItems(r.items); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [filter, refreshKey]);

  const onReopen = async (it: AnsweredGap) => {
    const isAnswered = it.status === "answered";
    const ok = await confirm({
      title: isAnswered ? "Reopen this cluster?" : "Restore this cluster?",
      description: isAnswered
        ? "It will appear in Open again. The underlying FAQ document is kept — delete it from Knowledge if you no longer want it."
        : "It will appear in Open again so you can answer or dismiss it.",
      confirmLabel: isAnswered ? "Reopen" : "Restore",
    });
    if (!ok) return;
    setReopening(it.id);
    try {
      await reopenGap(it.id);
      setItems((prev) => prev?.filter((x) => x.id !== it.id) ?? null);
      onChanged?.();
      toast.push(isAnswered ? "Reopened. Back in Open." : "Restored. Back in Open.");
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed", "error");
    } finally {
      setReopening(null);
    }
  };

  if (error) {
    return (
      <Card>
        <div className="px-5 py-4 text-sm text-danger">{error}</div>
      </Card>
    );
  }

  if (items === null) {
    return (
      <Card>
        <ul className="divide-y divide-line">
          {[0, 1, 2].map((i) => (
            <li key={i} className="space-y-2 px-5 py-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <div className="px-5 py-5">
          <EmptyState
            icon={CheckCircle2}
            title={filter === "dismissed" ? "Nothing dismissed yet" : filter === "answered" ? "Nothing answered yet" : "Nothing resolved yet"}
            description="Answers and dismissals you make in Open will show up here with the ability to reopen them."
          />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <ul className="divide-y divide-line">
        {items.map((it) => (
          <li key={it.id} className="px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <StatusBadge status={it.status} />
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-text-strong">
                    &ldquo;{it.original_question}&rdquo;
                  </p>
                </div>
                <p className="mt-1 pl-[26px] text-xs text-text-soft">
                  <span title={absolute(it.created_at)} className="cursor-default">
                    {it.status === "answered" ? "Answered" : "Dismissed"} {relative(it.created_at)}
                  </span>
                  <span className="mx-1.5 text-text-faint">·</span>
                  <span>
                    saved{" "}
                    <span className="tabular-nums">{it.occurrences_at_answer}</span>{" "}
                    {it.occurrences_at_answer === 1 ? "question" : "questions"}
                  </span>
                </p>
                {it.documents?.title && (
                  <p className="mt-1.5 ml-[26px] inline-flex items-center gap-1.5 rounded bg-surface-2 px-2 py-1 text-xs text-text-soft">
                    <FileText size={12} strokeWidth={1.75} />
                    {it.documents.title}
                    {!it.documents.is_active && (
                      <span className="ml-1 rounded bg-danger-soft px-1 text-2xs text-danger">deleted</span>
                    )}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReopen(it)}
                loading={reopening === it.id}
                iconLeft={!reopening && <RotateCcw size={12} strokeWidth={1.75} />}
                title={it.status === "answered" ? "Reopen and edit" : "Restore to Open"}
              >
                {it.status === "answered" ? "Reopen" : "Restore"}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function StatusBadge({ status }: { status: "answered" | "dismissed" }) {
  if (status === "answered") {
    return (
      <span className={cn("inline-flex shrink-0 items-center gap-1 rounded bg-success-soft px-1.5 py-0.5 text-2xs text-success")}>
        <CheckCircle2 size={11} strokeWidth={1.75} />
        Answered
      </span>
    );
  }
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-2xs text-text-soft")}>
      <XCircle size={11} strokeWidth={1.75} />
      Dismissed
    </span>
  );
}

// Re-export for callers that want to render a trash icon outside the row
// (e.g. a "Dismiss all off-topic" bulk action later).
export const _DismissIcon = Trash2;
